// Modified predictive-search.js with increased result limits and proper handle generation

document.addEventListener('DOMContentLoaded', function() {
  const searchInput = document.getElementById('header-search-input');
  const predictiveResults = document.getElementById('predictive-search-results');
  const searchLoading = document.getElementById('search-loading');
  const productResults = document.getElementById('predictive-product-results');
  const filmResults = document.getElementById('predictive-film-results');
  const viewAllLink = document.getElementById('view-all-results');
  
  // Check if search elements exist (avoid errors if not on a page with search)
  if (!searchInput) return;
  
  // CONFIGURATION - Adjust these values as needed
  const MAX_PRODUCT_RESULTS = 10; // Number of products to show (was 4)
  const MAX_FILM_RESULTS = 100;    // Number of films to show (was 4)
  
  // Track search state
  let searchTimeout;
  let filmsData = null;
  let isSearching = false;
  let debugMode = false; // Set to false for production
  let filmsDataLoaded = false;
  let pendingSearches = [];
  
  // Helper function to create handle like Shopify's handle filter
  function createHandle(title) {
    return title
      .toLowerCase()                    // Convert to lowercase
      .replace(/\./g, '-')             // Replace full stops with hyphens
      .replace(/[^\w\s-]/g, '')        // Remove all special characters except word chars, spaces, and hyphens
      .replace(/\s+/g, '-')            // Replace spaces with hyphens
      .replace(/-+/g, '-')             // Replace multiple hyphens with single hyphen
      .replace(/^-+|-+$/g, '');        // Remove leading/trailing hyphens
  }
  
  // Preload films data with improved error handling
  async function loadFilmsData(retryCount = 3) {
    try {
      searchLoading.style.display = 'flex';
      
      const response = await fetch('/search?view=films-json');
      if (!response.ok) {
        throw new Error(`HTTP error! status: ${response.status}`);
      }
      
      const rawData = await response.text(); // Get as text first for debugging
      
      // Debug the raw response if needed
      if (debugMode) {
        console.log('Raw JSON response (first 500 chars):', rawData.substring(0, 500));
      }
      
      // Try to parse the JSON
      try {
        filmsData = JSON.parse(rawData);
        filmsDataLoaded = true;
        
        if (debugMode) {
          console.log(`Loaded ${filmsData.length} film metaobjects`);
          // Log the first film's structure to inspect available fields
          if (filmsData.length > 0) {
            console.log('First film structure:', filmsData[0]);
          }
        }
        
        // Process any pending searches
        if (pendingSearches.length > 0) {
          const latestSearch = pendingSearches.pop();
          pendingSearches = []; // Clear other pending searches
          performPredictiveSearch(latestSearch);
        }
        
        searchLoading.style.display = 'none';
      } catch (parseError) {
        console.error('Error parsing films JSON:', parseError);
        console.error('Raw response begins with:', rawData.substring(0, 200));
        
        if (retryCount > 0) {
          console.log(`Retrying film data load (${retryCount} attempts left)...`);
          setTimeout(() => loadFilmsData(retryCount - 1), 1000);
        } else {
          searchLoading.style.display = 'none';
        }
      }
    } catch (error) {
      console.error('Error loading films data:', error);
      
      if (retryCount > 0) {
        console.log(`Retrying film data load (${retryCount} attempts left)...`);
        setTimeout(() => loadFilmsData(retryCount - 1), 1000);
      } else {
        searchLoading.style.display = 'none';
      }
    }
  }
  
  // Load films data when page loads
  loadFilmsData();
  
  // Show/hide predictive search
  searchInput.addEventListener('focus', () => {
    if (searchInput.value.length >= 2) {
      predictiveResults.classList.add('active');
    }
  });
  
  // Handle clicks outside search area to close dropdown
  document.addEventListener('click', (event) => {
    if (!event.target.closest('.header-search')) {
      predictiveResults.classList.remove('active');
    }
  });
  
  // Update search as user types
  searchInput.addEventListener('input', () => {
    const searchTerm = searchInput.value.trim();
    viewAllLink.href = `/search?q=${encodeURIComponent(searchTerm)}`;
    
    if (searchTerm.length >= 2) {
      predictiveResults.classList.add('active');
      searchLoading.style.display = 'flex';
      
      // Clear previous timeout
      if (searchTimeout) {
        clearTimeout(searchTimeout);
      }
      
      // Set a small delay to avoid too many requests
      searchTimeout = setTimeout(() => {
        performPredictiveSearch(searchTerm);
      }, 300);
    } else {
      predictiveResults.classList.remove('active');
    }
  });
  
  // Handle form submission
  document.getElementById('header-search-form').addEventListener('submit', (event) => {
    const searchTerm = searchInput.value.trim();
    if (searchTerm.length === 0) {
      event.preventDefault();
    }
  });
  
  // Helper function to safely check if a string property includes the search term
  const safeStringSearch = (value, term) => {
    if (value === null || value === undefined) return false;
    
    // Convert to string if it's not already a string
    let strValue;
    try {
      if (typeof value === 'object') {
        strValue = JSON.stringify(value);
      } else {
        strValue = String(value);
      }
      return strValue.toLowerCase().includes(term);
    } catch (e) {
      if (debugMode) console.error('Error in safeStringSearch:', e, value);
      return false;
    }
  };
  
  // Search films from preloaded data with improved error handling
  function searchFilms(searchTerm) {
    return new Promise((resolve) => {
      if (!filmsDataLoaded || !filmsData) {
        if (debugMode) {
          console.log('Film data not yet loaded, will search later');
        }
        resolve([]);
        return;
      }
      
      const term = searchTerm.toLowerCase();
      
      if (debugMode) {
        console.log(`Searching for "${term}" in ${filmsData.length} films`);
      }
      
      try {
        const results = filmsData.filter(film => {
          // Skip invalid films
          if (!film || typeof film !== 'object') return false;
          
          // Match against all relevant fields
          return (
            safeStringSearch(film.title, term) ||
            safeStringSearch(film.logline, term) ||
            safeStringSearch(film.synopsis, term) ||
            safeStringSearch(film.director, term) ||
            safeStringSearch(film.cast, term) ||
            safeStringSearch(film.genres, term) ||
            safeStringSearch(film.year_of_release, term) ||
            safeStringSearch(film.country, term) ||
            safeStringSearch(film.language, term) ||
            safeStringSearch(film.studio, term)
          );
        }).slice(0, MAX_FILM_RESULTS); // CHANGED: Increased limit from 4 to MAX_FILM_RESULTS
        
        if (debugMode) {
          console.log(`Found ${results.length} matching films for "${term}"`);
        }
        
        resolve(results);
      } catch (error) {
        console.error('Error in searchFilms:', error);
        resolve([]);
      }
    });
  }
  
  // Perform predictive search
  async function performPredictiveSearch(searchTerm) {
    if (isSearching) {
      // Queue this search to run after current search completes
      pendingSearches.push(searchTerm);
      return;
    }
    
    isSearching = true;
    searchLoading.style.display = 'flex';
    
    // If films data is not loaded yet, queue the search
    if (!filmsDataLoaded) {
      pendingSearches.push(searchTerm);
      
      if (debugMode) {
        console.log(`Films data not yet loaded, queuing search for "${searchTerm}"`);
      }
      
      // Make sure we display the loading indicator
      searchLoading.style.display = 'flex';
      return;
    }
    
    try {
      // 1. Search products via Shopify predictive search endpoint
      if (debugMode) {
        console.log(`Searching Shopify products for: "${searchTerm}"`);
      }
      
      // CHANGED: Increased product limit from 4 to MAX_PRODUCT_RESULTS
      const productSearchResponse = await fetch(`/search/suggest.json?q=${encodeURIComponent(searchTerm)}&resources[type]=product&resources[limit]=${MAX_PRODUCT_RESULTS}&resources[options][unavailable_products]=hide`);
      const productData = await productSearchResponse.json();
      const products = productData.resources.results.products;
      
      if (debugMode) {
        console.log(`Found ${products.length} matching products`);
      }
      
      // 2. Search films using preloaded data
      if (debugMode) {
        console.log(`Searching films for: "${searchTerm}"`);
      }
      
      const filmResults = await searchFilms(searchTerm);
      
      // Display results
      displayProductResults(products);
      displayFilmResults(filmResults);
      
    } catch (error) {
      console.error('Error in predictive search:', error);
      productResults.innerHTML = '<p class="no-results">Error loading results</p>';
      filmResults.innerHTML = '<p class="no-results">Error loading results</p>';
    } finally {
      searchLoading.style.display = 'none';
      isSearching = false;
      
      // Process any pending searches
      if (pendingSearches.length > 0) {
        const nextSearch = pendingSearches.pop();
        pendingSearches = []; // Clear other pending searches
        performPredictiveSearch(nextSearch);
      }
    }
  }

  // Display product results
  function displayProductResults(products) {
    if (!products || products.length === 0) {
      productResults.innerHTML = '<p class="no-results">No products found</p>';
      return;
    }
    
    let html = '<h3>Shop</h3><ul class="predictive-products">';
    
    products.forEach(product => {
      try {
       // Extract the main title (without variant info)
const mainTitle = product.title.split(' - ')[0];

// Use the URL Shopify's predictive search returns
const productUrl = product.url;

html += `
  <li class="predictive-item">
    <a href="${productUrl}" class="predictive-link">
              <div class="predictive-image">
                ${product.image ? 
                  `<img src="${product.image}" alt="${mainTitle}">` : 
                  `<div class="no-image"></div>`
                }
              </div>
              <div class="predictive-info">
                <p class="predictive-title">${mainTitle}</p>
                <p class="predictive-price">£${product.price}</p>
              </div>
            </a>
          </li>
        `;
      } catch (error) {
        console.error('Error displaying product:', error, product);
      }
    });
    
    html += '</ul>';
    productResults.innerHTML = html;
  }
  
  // Display film results
  function displayFilmResults(films) {
    if (!films || films.length === 0) {
      filmResults.innerHTML = '<p class="no-results">No films found</p>';
      return;
    }
    
    let html = '<h3>Watch</h3><ul class="predictive-films">';
    
    films.forEach(film => {
      try {
        // Safe display of film properties with proper null/undefined checks
        const filmTitle = film.title || 'Untitled Film';
        const filmHandle = film.handle || createHandle(filmTitle);
        const primaryImage = film.primary_image || '';
        const yearOfRelease = film.year_of_release || '';
        const director = film.director || '';
        
        html += `
          <li class="predictive-item">
            <a href="/pages/films/${filmHandle}" class="predictive-link">
              <div class="predictive-image">
                ${primaryImage ? 
                  `<img src="${primaryImage}" alt="${filmTitle}">` : 
                  `<div class="no-image"></div>`
                }
              </div>
              <div class="predictive-info">
                <p class="predictive-title">${filmTitle}</p>
                ${yearOfRelease ? `<p class="predictive-year">${yearOfRelease}</p>` : ''}
              </div>
            </a>
          </li>
        `;
      } catch (error) {
        console.error('Error displaying film:', error, film);
      }
    });
    
    html += '</ul>';
    filmResults.innerHTML = html;
  }
});